import { useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { logEvent } from '../services/loggingService';
import { langchainJobScraper } from '../services/langchainService';
import { scrapeJobDetails } from '../services/optimizationService';
import type { JobData, JobScrapingResult } from '../types/optimization';

export type JobUrlItem = {
  url: string;
  status: 'idle' | 'scraping' | 'success' | 'failed';
  error: string | null;
  abortController?: AbortController;
  optimized?: boolean;
};

export const useJobScraping = () => {
  const [jobUrls, setJobUrls] = useState<JobUrlItem[]>([{ 
    url: '', 
    status: 'idle', 
    error: null 
  }]);
  const [jobDataArray, setJobDataArray] = useState<JobScrapingResult[]>([]);
  const [isScrapingJob, setIsScrapingJob] = useState(false);
  const [scrapingError, setScrapingError] = useState<string | null>(null);
  const abortControllersRef = useRef<Map<number, AbortController>>(new Map());

  // Check if a URL is from a supported job portal
  const isValidJobPortalUrl = useCallback((url: string): boolean => {
    const lowerUrl = url.toLowerCase();
    return lowerUrl.includes('linkedin.com/jobs/view/') || 
           lowerUrl.includes('indeed.com/viewjob') ||
           lowerUrl.includes('glassdoor.com/job-listing/') ||
           lowerUrl.includes('naukri.com/job-listings') ||
           lowerUrl.includes('naukri.com') ||
           lowerUrl.includes('internshala.com/internship/') ||
           lowerUrl.includes('internshala.com') ||
           lowerUrl.includes('monster.com/job-openings/') ||
           lowerUrl.includes('monster.com') ||
           lowerUrl.includes('bayt.com/job/') ||
           lowerUrl.includes('bayt.com') ||
           lowerUrl.includes('foundit.in/jobs/') ||
           lowerUrl.includes('foundit.in') ||
           lowerUrl.includes('linkedin.com') ||
           lowerUrl.includes('indeed.com') ||
           lowerUrl.includes('glassdoor.com');
  }, []);

  // Validate job URL
  const validateJobUrl = useCallback((url: string): string | null => {
    // Auto-trim the URL
    const trimmedUrl = url.trim();
    
    if (!trimmedUrl) {
      return 'Job URL is required';
    }
    
    try {
      new URL(trimmedUrl);
    } catch (e) {
      return 'Invalid URL format. Please enter a valid URL.';
    }
    
    if (!isValidJobPortalUrl(trimmedUrl)) {
      return 'URL must be from a supported job site (LinkedIn, Indeed, Glassdoor, Naukri, Internshala, Monster, Bayt, Foundit)';
    }
    
    return null;
  }, [isValidJobPortalUrl]);

  // Mark a job as optimized
  const markJobAsOptimized = useCallback((jobId: string | number) => {
    setJobDataArray(prev => prev.map((item, index) => {
      if ((typeof jobId === 'number' && index === jobId) || 
          (typeof jobId === 'string' && item.data?.jobId === jobId)) {
        if (item.data) {
          return {
            ...item,
            data: {
              ...item.data,
              optimized: true
            }
          };
        }
      }
      return item;
    }));
  }, []);

  // Reset optimized status for all jobs
  const resetOptimizedStatus = useCallback(() => {
    setJobDataArray(prev => prev.map(item => {
      if (item.data) {
        return {
          ...item,
          data: {
            ...item.data,
            optimized: false
          }
        };
      }
      return item;
    }));
  }, []);

  // Extract skills from job data
  const extractSkillsFromJobData = useCallback((jobData: any): string[] => {
    if (!jobData) return [];
    
    const skills: string[] = [];
    const text = JSON.stringify(jobData).toLowerCase();
    
    const skillsDatabase = {
      programming: [
        'javascript', 'typescript', 'python', 'java', 'c#', 'c++', 'c', 'go', 'golang',
        'rust', 'swift', 'kotlin', 'scala', 'ruby', 'php', 'perl', 'r', 'matlab',
        'objective-c', 'dart', 'elixir', 'haskell', 'clojure', 'f#', 'vb.net'
      ],
      web: [
        'html', 'css', 'sass', 'scss', 'less', 'bootstrap', 'tailwind css',
        'react', 'angular', 'vue', 'vue.js', 'svelte', 'ember', 'backbone',
        'jquery', 'webpack', 'vite', 'parcel', 'rollup', 'babel', 'eslint'
      ],
      backend: [
        'node.js', 'express', 'fastify', 'koa', 'django', 'flask', 'fastapi',
        'spring', 'spring boot', 'hibernate', '.net', 'asp.net', 'laravel',
        'symfony', 'rails', 'sinatra', 'gin', 'echo', 'fiber'
      ],
      databases: [
        'sql', 'mysql', 'postgresql', 'sqlite', 'oracle', 'sql server',
        'mongodb', 'cassandra', 'redis', 'elasticsearch', 'dynamodb',
        'couchdb', 'neo4j', 'influxdb', 'clickhouse', 'snowflake'
      ],
      cloud: [
        'aws', 'azure', 'gcp', 'google cloud', 'docker', 'kubernetes', 'k8s',
        'terraform', 'ansible', 'jenkins', 'gitlab ci', 'github actions',
        'circleci', 'travis ci', 'helm', 'istio', 'prometheus', 'grafana'
      ],
      data: [
        'machine learning', 'deep learning', 'artificial intelligence', 'ai', 'ml',
        'tensorflow', 'pytorch', 'keras', 'scikit-learn', 'pandas', 'numpy',
        'jupyter', 'apache spark', 'hadoop', 'kafka', 'airflow', 'dbt',
        'tableau', 'power bi', 'looker', 'data science', 'statistics'
      ],
      mobile: [
        'ios', 'android', 'react native', 'flutter', 'xamarin', 'ionic',
        'cordova', 'phonegap', 'swift', 'objective-c', 'kotlin', 'java'
      ],
      methodologies: [
        'agile', 'scrum', 'kanban', 'lean', 'waterfall', 'devops', 'ci/cd',
        'tdd', 'bdd', 'pair programming', 'code review', 'microservices',
        'serverless', 'rest', 'graphql', 'soap', 'api design', 'mvc'
      ],
      tools: [
        'git', 'github', 'gitlab', 'bitbucket', 'jira', 'confluence', 'slack',
        'figma', 'sketch', 'adobe xd', 'photoshop', 'illustrator', 'postman',
        'insomnia', 'swagger', 'openapi', 'vs code', 'intellij', 'eclipse'
      ],
      soft: [
        'leadership', 'team management', 'project management', 'communication',
        'problem solving', 'analytical thinking', 'strategic planning',
        'mentoring', 'coaching', 'stakeholder management', 'cross-functional',
        'collaboration', 'innovation', 'creativity', 'adaptability'
      ],
      business: [
        'product management', 'business analysis', 'requirements gathering',
        'user experience', 'ux', 'user interface', 'ui', 'design thinking',
        'market research', 'competitive analysis', 'roadmap planning',
        'kpi', 'metrics', 'analytics', 'a/b testing', 'conversion optimization'
      ],
      security: [
        'cybersecurity', 'information security', 'penetration testing',
        'vulnerability assessment', 'oauth', 'jwt', 'ssl', 'tls', 'encryption',
        'gdpr', 'hipaa', 'pci dss', 'sox', 'iso 27001', 'nist'
      ],
      fintech: [
        'fintech', 'payments', 'banking', 'trading', 'blockchain', 'cryptocurrency',
        'defi', 'regulatory compliance', 'risk management', 'fraud detection'
      ],
      healthcare: [
        'healthcare', 'telemedicine', 'ehr', 'emr', 'fhir', 'hl7', 'medical devices',
        'clinical trials', 'pharmaceutical', 'biotech', 'genomics'
      ],
      ecommerce: [
        'e-commerce', 'retail', 'inventory management', 'supply chain',
        'logistics', 'payment processing', 'shopping cart', 'marketplace'
      ]
    };
    
    const allSkills = Object.values(skillsDatabase).flat();
    
    const multiWordSkills = [
      'machine learning', 'deep learning', 'artificial intelligence', 'data science',
      'computer vision', 'natural language processing', 'big data', 'cloud computing',
      'software engineering', 'full stack', 'front end', 'back end', 'user experience',
      'user interface', 'product management', 'project management', 'business analysis',
      'quality assurance', 'test automation', 'continuous integration', 'continuous deployment',
      'version control', 'code review', 'pair programming', 'agile development',
      'scrum master', 'product owner', 'technical lead', 'team lead', 'senior developer',
      'software architect', 'system design', 'database design', 'api design',
      'microservices architecture', 'event driven architecture', 'service oriented architecture'
    ];
    
    multiWordSkills.forEach(phrase => {
      const regex = new RegExp(`\\b${phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
      if (regex.test(text)) {
        const formattedSkill = phrase.split(' ').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
        
        if (!skills.some(s => s.toLowerCase() === formattedSkill.toLowerCase())) {
          skills.push(formattedSkill);
        }
      }
    });
    
    allSkills.forEach(skill => {
      const isPartOfMultiWord = multiWordSkills.some(phrase => 
        phrase.includes(skill) && skills.some(s => s.toLowerCase().includes(skill))
      );
      
      if (!isPartOfMultiWord) {
        const regex = new RegExp(`\\b${skill.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
        if (regex.test(text)) {
          const formattedSkill = skill.includes('.') ? skill : 
            skill.split(' ').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ');
          
          if (!skills.some(s => s.toLowerCase() === formattedSkill.toLowerCase())) {
            skills.push(formattedSkill);
          }
        }
      }
    });
    
    if (jobData.requirements && Array.isArray(jobData.requirements)) {
      jobData.requirements.forEach((req: string) => {
        extractSkillsFromText(req, skills, allSkills);
      });
    }
    
    if (jobData.responsibilities && Array.isArray(jobData.responsibilities)) {
      jobData.responsibilities.forEach((resp: string) => {
        extractSkillsFromText(resp, skills, allSkills);
      });
    }
    
    if (jobData.description && typeof jobData.description === 'string') {
      extractSkillsFromText(jobData.description, skills, allSkills);
    }
    
    const sortedSkills = skills.sort((a, b) => {
      const aTechnical = skillsDatabase.programming.includes(a.toLowerCase()) ||
                       skillsDatabase.web.includes(a.toLowerCase()) ||
                       skillsDatabase.backend.includes(a.toLowerCase()) ||
                       skillsDatabase.cloud.includes(a.toLowerCase()) ||
                       skillsDatabase.data.includes(a.toLowerCase());
      
      const bTechnical = skillsDatabase.programming.includes(b.toLowerCase()) ||
                       skillsDatabase.web.includes(b.toLowerCase()) ||
                       skillsDatabase.backend.includes(b.toLowerCase()) ||
                       skillsDatabase.cloud.includes(b.toLowerCase()) ||
                       skillsDatabase.data.includes(b.toLowerCase());
      
      if (aTechnical && !bTechnical) return -1;
      if (!aTechnical && bTechnical) return 1;
      return 0;
    });
    
    return sortedSkills.slice(0, 25);
  }, []);
  
  const extractSkillsFromText = useCallback((text: string, existingSkills: string[], allSkills: string[]) => {
    if (!text || typeof text !== 'string') return;
    
    const lowerText = text.toLowerCase();
    
    const skillPatterns = [
      /(?:experience|proficiency|expertise|knowledge|skills?) (?:in|with|using|of) ([\w\s.+-]+?)(?:\s*[,;.]|$)/gi,
      /(?:proficient|skilled|experienced|familiar) (?:in|with|using) ([\w\s.+-]+?)(?:\s*[,;.]|$)/gi,
      /(?:strong|solid|deep|extensive) (?:background|experience|knowledge) (?:in|with|of) ([\w\s.+-]+?)(?:\s*[,;.]|$)/gi,
      /(\d+)\+?\s*(?:years?|yrs?) (?:of )?(?:experience )?(?:in|with|using) ([\w\s.+-]+?)(?:\s*[,;.]|$)/gi,
      /(?:using|leveraging|implementing|working with|built with) (?:applications?|systems?|solutions?|platforms?|services?) (?:using|with|in) ([\w\s.+-]+?)(?:\s*[,;.]|$)/gi,
      /(?:must have|required|essential|mandatory):?\s*([\w\s.+-]+?)(?:\s*[,;.]|$)/gi,
      /(?:preferred|nice to have|bonus|plus):?\s*([\w\s.+-]+?)(?:\s*[,;.]|$)/gi,
      /(?:develop|build|create|implement|design|maintain|support|work on|use) (?:applications?|systems?|solutions?|platforms?|services?) (?:using|with|in) ([\w\s.+-]+?)(?:\s*[,;.]|$)/gi
    ];
    
    skillPatterns.forEach(pattern => {
      let match;
      while ((match = pattern.exec(lowerText)) !== null) {
        const extractedText = match[match.length - 1]?.trim();
        if (extractedText && extractedText.length > 1 && extractedText.length < 50) {
          const parts = extractedText.split(/[,;&|\/\n\r]+/).map(part => part.trim());
          
          parts.forEach(part => {
            if (part.length > 1 && part.length < 30) {
              const matchingSkill = allSkills.find(skill => 
                skill.toLowerCase() === part.toLowerCase() ||
                part.toLowerCase().includes(skill.toLowerCase()) ||
                skill.toLowerCase().includes(part.toLowerCase())
              );
              
              if (matchingSkill) {
                const formattedSkill = matchingSkill.split(' ').map(word => 
                  word.charAt(0).toUpperCase() + word.slice(1)
                ).join(' ');
                
                if (!existingSkills.some(s => s.toLowerCase() === formattedSkill.toLowerCase())) {
                  existingSkills.push(formattedSkill);
                }
              } else {
                if (/^[\w\s.+-]+$/.test(part) && !/^\d+$/.test(part)) {
                  const formattedSkill = part.split(' ').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ');
                  
                  if (!existingSkills.some(s => s.toLowerCase() === formattedSkill.toLowerCase())) {
                    existingSkills.push(formattedSkill);
                  }
                }
              }
            }
          });
        }
      }
    });
    
    allSkills.forEach(skill => {
      const regex = new RegExp(`\\b${skill.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
      if (regex.test(lowerText)) {
        const formattedSkill = skill.split(' ').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
        
        if (!existingSkills.some(s => s.toLowerCase() === formattedSkill.toLowerCase())) {
          existingSkills.push(formattedSkill);
        }
      }
    });
  }, []);

  // Add a new job URL
  const addJobUrl = useCallback(() => {
    if (jobUrls.length < 5) {
      setJobUrls(prev => [...prev, { url: '', status: 'idle', error: null }]);
      
      // Also ensure jobDataArray is synchronized
      setJobDataArray(prev => {
        const newArray = [...prev];
        while (newArray.length < jobUrls.length + 1) {
          newArray.push({ url: '', data: null, error: null });
        }
        return newArray;
      });
      
      logEvent({
        level: 'info',
        action: 'add_job_url',
        details: { currentCount: jobUrls.length, newCount: jobUrls.length + 1 }
      });
    }
  }, [jobUrls]);

  // Remove a job URL
  const removeJobUrl = useCallback((index: number) => {
    // Validate index bounds
    if (index < 0 || index >= jobUrls.length) {
      console.error(`Invalid index ${index} for removal from jobUrls array of length ${jobUrls.length}`);
      return;
    }

    if (jobUrls.length > 1) {
      if (abortControllersRef.current.has(index)) {
        abortControllersRef.current.get(index)?.abort();
        abortControllersRef.current.delete(index);
      }
      
      setJobUrls(prev => prev.filter((_, i) => i !== index));
      setJobDataArray(prev => prev.filter((_, i) => i !== index));
      
      logEvent({
        level: 'info',
        action: 'remove_job_url',
        details: { removedIndex: index, currentCount: jobUrls.length, newCount: jobUrls.length - 1 }
      });
    }
  }, [jobUrls]);

  // Update a job URL
  const updateJobUrl = useCallback((index: number, value: string) => {
    // Validate index bounds
    if (index < 0 || index >= jobUrls.length) {
      console.error(`Invalid index ${index} for jobUrls array of length ${jobUrls.length}`);
      return;
    }

    // Auto-trim the URL value
    const trimmedValue = value.trim();
    const validationError = validateJobUrl(trimmedValue);
    
    setJobUrls(prev => {
      const newUrls = [...prev];
      // Ensure the item exists before updating
      if (!newUrls[index]) {
        newUrls[index] = { url: '', status: 'idle', error: null };
      }
      newUrls[index] = { 
        ...newUrls[index], 
        url: trimmedValue, 
        status: 'idle', 
        error: validationError 
      };
      return newUrls;
    });
    
    setScrapingError(null);
    
    const currentJobData = jobDataArray[index];
    if (currentJobData && currentJobData.url && trimmedValue !== currentJobData.url) {
      setJobDataArray(prev => {
        const newArray = [...prev];
        // Ensure array is large enough and item exists
        while (newArray.length <= index) {
          newArray.push({ url: '', data: null, error: null });
        }
        if (newArray[index]) {
          newArray[index] = { ...newArray[index], data: null, error: null };
        }
        return newArray;
      });
    }
  }, [jobDataArray, validateJobUrl]);

  // Validate all job URLs
  const validateAllJobUrls = useCallback((): string | null => {
    const nonEmptyUrls = jobUrls.filter(item => item.url.trim());
    if (nonEmptyUrls.length === 0) {
      return 'Please enter at least one job URL';
    }
    
    for (let i = 0; i < jobUrls.length; i++) {
      const url = jobUrls[i].url;
      if (url.trim()) {
        const error = validateJobUrl(url);
        if (error) {
          return `URL #${i + 1}: ${error}`;
        }
      }
    }
    
    return null;
  }, [jobUrls, validateJobUrl]);

  // Stop scraping for a specific URL
  const cancelScraping = useCallback((index: number) => {
    if (abortControllersRef.current.has(index)) {
      abortControllersRef.current.get(index)?.abort();
      abortControllersRef.current.delete(index);
      
      setJobUrls(prev => {
        const newUrls = [...prev];
        if (newUrls[index]) {
          newUrls[index] = { 
            ...newUrls[index], 
            status: 'failed', 
            error: 'Scraping cancelled by user' 
          };
        }
        return newUrls;
      });
      
      logEvent({
        level: 'info',
        action: 'cancel_job_scraping',
        details: { index, url: jobUrls[index]?.url }
      });
    }
  }, [jobUrls]);

  // Retry scraping for a specific URL
  const retryScraping = useCallback((index: number) => {
    if (index >= 0 && index < jobUrls.length) {
      setJobUrls(prev => {
        const newUrls = [...prev];
        newUrls[index] = { ...newUrls[index], status: 'idle', error: null };
        return newUrls;
      });
      
      handleJobScraping(index);
      
      logEvent({
        level: 'info',
        action: 'retry_job_scraping',
        details: { index, url: jobUrls[index]?.url }
      });
    }
  }, [jobUrls]);

  // Handle job URL scraping for multiple URLs or a specific URL
  const handleJobScraping = useCallback(async (specificIndex?: number | React.MouseEvent) => {
    // Fix: If specificIndex is an event object, extract the actual index or set to undefined
    let actualIndex: number | undefined;
    if (typeof specificIndex === 'number') {
      actualIndex = specificIndex;
    } else if (specificIndex && typeof specificIndex === 'object' && 'currentTarget' in specificIndex) {
      // It's an event object, set to undefined to scrape all
      actualIndex = undefined;
    } else {
      actualIndex = specificIndex as number | undefined;
    }
    
    try {
      console.log('[useJobScraping] handleJobScraping called with:', { 
        originalParam: typeof specificIndex, 
        actualIndex, 
        jobUrlsLength: jobUrls?.length 
      });
      
      // Validate jobUrls array exists and has content
      if (!jobUrls || jobUrls.length === 0) {
        console.error('[useJobScraping] No jobUrls array or empty array');
        setScrapingError('No job URLs available to scrape');
        return;
      }

      // Log current jobUrls state
      console.log('[useJobScraping] Current jobUrls state:', jobUrls.map((item, i) => ({
        index: i,
        url: item?.url || 'undefined',
        status: item?.status || 'undefined',
        hasError: !!item?.error
      })));

      const indicesToScrape = actualIndex !== undefined 
        ? [actualIndex] 
        : jobUrls.map((_, index) => index).filter(index => {
            const jobUrl = jobUrls[index];
            console.log(`[useJobScraping] Checking index ${index}:`, {
              exists: !!jobUrl,
              hasUrl: !!jobUrl?.url,
              urlType: typeof jobUrl?.url,
              urlValue: jobUrl?.url || 'undefined'
            });
            
            if (!jobUrl || typeof jobUrl.url !== 'string') {
              console.warn(`Invalid job URL at index ${index}:`, jobUrl);
              return false;
            }
            const url = jobUrl.url;
            return url.trim() && isValidJobPortalUrl(url);
          });
      
      console.log('[useJobScraping] Indices to scrape:', indicesToScrape);
      
      if (indicesToScrape.length === 0) {
        console.error('[useJobScraping] No valid URLs to scrape');
        setScrapingError('Please enter at least one valid job portal URL');
        return;
      }
      
      setIsScrapingJob(true);
      setScrapingError(null);
      await logEvent({
        level: 'info',
        action: 'job_fetching_start',
        details: { 
          urlCount: indicesToScrape.length,
          urls: indicesToScrape.map(i => jobUrls[i].url)
        }
      });      
      
      // Initialize jobDataArray ONLY if it's empty or size mismatch
      // Don't reset if we already have data (avoids clearing previous successful scrapes)
      if (jobDataArray.length === 0) {
        console.log('[useJobScraping] Initializing empty jobDataArray');
        const initialResults = jobUrls.map((item) => ({
          url: item.url,
          data: null as JobData | null,
          error: null as string | null
        }));
        setJobDataArray(initialResults);
      } else if (jobDataArray.length !== jobUrls.length) {
        console.log('[useJobScraping] Resizing jobDataArray to match jobUrls length');
        // Resize array but preserve existing data
        const newResults = jobUrls.map((item, index) => ({
          url: item.url,
          data: jobDataArray[index]?.data || null,
          error: jobDataArray[index]?.error || null
        }));
        setJobDataArray(newResults);
      }
      
      try {
        // Check authentication - but allow guest users too
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        const { data: { session } } = await supabase.auth.getSession();
        
        if (user) {
          console.log('[useJobScraping] User authenticated:', user.id);
        } else {
          console.log('[useJobScraping] Guest user - proceeding without authentication');
        }
        
        // Session is optional - guest users can scrape jobs too
        if (!session) {
          console.log('[useJobScraping] No session - using guest mode for job scraping');
        }
        
        for (const index of indicesToScrape) {
          const jobUrlItem = jobUrls[index];
          console.log(`[useJobScraping] Processing index ${index}:`, {
            exists: !!jobUrlItem,
            url: jobUrlItem?.url || 'undefined',
            urlLength: jobUrlItem?.url?.length || 0
          });
          
          if (!jobUrlItem || typeof jobUrlItem.url !== 'string') {
            console.error(`Invalid job URL item at index ${index}:`, jobUrlItem);
            continue;
          }
          
          const url = jobUrlItem.url.trim(); // Auto-trim the URL
          console.log(`[useJobScraping] Trimmed URL for index ${index}:`, url);
          
          if (!url.trim() || !isValidJobPortalUrl(url)) {
            console.warn(`Skipping invalid URL at index ${index}:`, url);
            continue;
          }
          
          const existingJobDataIndex = jobDataArray.findIndex(item => 
            item.url === url && item.data !== null && !item.error
          );
          
          if (existingJobDataIndex !== -1) {
            setJobUrls(prev => {
              const newUrls = [...prev];
              newUrls[index] = { ...newUrls[index], status: 'success', error: null };
              return newUrls;
            });
            
            setJobDataArray(prev => {
              const newArray = [...prev];
              newArray[index] = {
                url,
                data: { 
                  ...prev[existingJobDataIndex].data!,
                  jobIndex: index,
                  optimized: false
                },
                error: null
              };
              return newArray;
            });
            
            await logEvent({
              level: 'info',
              action: 'job_scraping_duplicate_reused',
              details: { 
                url,
                index,
                originalIndex: existingJobDataIndex,
                jobTitle: jobDataArray[existingJobDataIndex].data?.title,
                company: jobDataArray[existingJobDataIndex].data?.company
              }
            });
            
            continue;
          }
          
          setJobUrls(prev => {
            const newUrls = [...prev];
            newUrls[index] = { ...newUrls[index], status: 'scraping', error: null };
            return newUrls;
          });
          
          try {
            const abortController = new AbortController();
            abortControllersRef.current.set(index, abortController);
            
            // Strategy: Use Edge Function for major portals (CORS protected), LangChain for others
            let scrapedJobData;
            let usedFallback = false;
            
            // List of portals with CORS protection - use edge function for these
            const corsProtectedPortals = [
              'linkedin.com',
              'naukri.com',
              'indeed.com',
              'glassdoor.com',
              'monster.com',
              'internshala.com',
              'shine.com',
              'timesjobs.com',
              'foundit.in',
              'instahyre.com',
              'bayt.com'
            ];
            
            const urlLower = url.toLowerCase();
            const isCorsProtected = corsProtectedPortals.some(portal => urlLower.includes(portal));
            
            try {
              if (isCorsProtected) {
                // Major job portals: Always use edge function (server-side) to avoid CORS
                console.log('[useJobScraping] CORS-protected portal detected, using edge function...');
                const { jobId, jobData } = await scrapeJobDetails(url);
                
                console.log('[useJobScraping] Edge function returned data:', jobData);
                
                // Use the data directly from the edge function response
                scrapedJobData = {
                  jobId: jobId,
                  title: jobData.title || jobData.job_title || '',
                  company: jobData.company || jobData.company_name || '',
                  location: jobData.location || '',
                  description: jobData.description || '',
                  requirements: Array.isArray(jobData.requirements) ? jobData.requirements : [],
                  qualifications: Array.isArray(jobData.qualifications) ? jobData.qualifications : [],
                  responsibilities: Array.isArray(jobData.responsibilities) ? jobData.responsibilities : [],
                  benefits: Array.isArray(jobData.benefits) ? jobData.benefits : [],
                  salary: jobData.salary || '',
                  employmentType: jobData.employmentType || jobData.employment_type || '',
                  experienceLevel: jobData.experienceLevel || jobData.experience_level || '',
                  skills: Array.isArray(jobData.skills) ? jobData.skills : [],
                  metadata: {
                    scrapedAt: new Date().toISOString(),
                    url: url,
                    confidence: 0.95,
                    processingTime: 0
                  }
                };
                usedFallback = true;
                console.log('[useJobScraping] Edge function scraping successful!');
              } else {
                // Other URLs: Try LangChain first (browser-based scraping)
                console.log('[useJobScraping] Attempting LangChain direct scraping for:', url);
                scrapedJobData = await langchainJobScraper.scrapeJobUrl(url);
                console.log('[useJobScraping] LangChain scraping successful!');
              }
              
            } catch (primaryError) {
              // Primary method failed, try fallback (only for non-CORS-protected URLs)
              if (!isCorsProtected) {
                // For non-CORS-protected URLs: Try edge function as fallback
                console.warn('[useJobScraping] LangChain scraping failed:', primaryError);
                console.log('[useJobScraping] Attempting Supabase edge function fallback...');
              
                try {
                  // FALLBACK METHOD: Supabase edge function (server-side scraping)
                  const { jobId, jobData } = await scrapeJobDetails(url);
                  
                  console.log('[useJobScraping] Fallback edge function returned data:', jobData);
                  
                  // Use the data directly from the edge function response
                  scrapedJobData = {
                    jobId: jobId,
                    title: jobData.title || jobData.job_title || 'Job Title',
                    company: jobData.company || jobData.company_name || 'Company',
                    location: jobData.location || 'Location',
                    description: jobData.description || '',
                    requirements: Array.isArray(jobData.requirements) ? jobData.requirements : [],
                    skills: Array.isArray(jobData.skills) ? jobData.skills : extractSkillsFromJobData(jobData),
                    metadata: {
                      portal: url.includes('linkedin') ? 'linkedin' : 
                              url.includes('indeed') ? 'indeed' : 
                              url.includes('glassdoor') ? 'glassdoor' :
                              url.includes('naukri') ? 'naukri' :
                              url.includes('monster') ? 'monster' :
                              url.includes('shine') ? 'shine' :
                              url.includes('timesjobs') ? 'timesjobs' :
                              'other',
                      scrapedAt: new Date(),
                      confidence: 0.75, // Lower confidence for fallback
                      processingTime: 0
                    }
                  };
                  
                  usedFallback = true;
                  console.log('[useJobScraping] Supabase fallback successful!');
                  
                } catch (supabaseError) {
                  // Both methods failed for non-CORS-protected URLs
                  console.error('[useJobScraping] Both LangChain and Supabase scraping failed');
                  console.error('[useJobScraping] LangChain error:', primaryError);
                  console.error('[useJobScraping] Supabase error:', supabaseError);
                  throw new Error(`Failed to scrape job. Please check if the URL is accessible and try again.`);
                }
              } else {
                // Check if this is a guest user auth issue
                if (primaryError instanceof Error && primaryError.message === 'GUEST_USER_FALLBACK_REQUIRED') {
                  console.log('[useJobScraping] Guest user detected - allowing manual job entry');
                  // Return a special marker that tells the UI to show manual entry option
                  throw new Error('GUEST_AUTH_REQUIRED');
                }
                
                // For CORS-protected URLs: Edge function already failed, no fallback available
                const portalName = urlLower.includes('linkedin') ? 'LinkedIn' :
                                   urlLower.includes('naukri') ? 'Naukri' :
                                   urlLower.includes('indeed') ? 'Indeed' :
                                   urlLower.includes('glassdoor') ? 'Glassdoor' : 'this portal';
                console.error(`[useJobScraping] ${portalName} scraping via edge function failed:`, primaryError);
                throw new Error(`Failed to scrape ${portalName} job. ${primaryError instanceof Error ? primaryError.message : 'Please try again later.'}`);
              }
            }
            
            console.log('[useJobScraping] Job scraping completed:', {
              jobId: scrapedJobData.jobId,
              title: scrapedJobData.title,
              company: scrapedJobData.company,
              portal: scrapedJobData.metadata.portal,
              method: usedFallback ? 'Supabase (fallback)' : 'LangChain (direct)',
              confidence: scrapedJobData.metadata.confidence
            });
            
            abortControllersRef.current.delete(index);
            
            // Transform LangChain data to our JobData format
            const transformedJobData: JobData = {
              jobId: scrapedJobData.jobId,
              title: scrapedJobData.title || 'Job Title',
              company: scrapedJobData.company || 'Company',
              location: scrapedJobData.location || 'Location',
              responsibilities: [], // LangChain doesn't separate these, they're in description
              requirements: scrapedJobData.requirements || [],
              skills: scrapedJobData.skills || [],
              description: scrapedJobData.description || '',
              jobUrl: url,
              jobIndex: index,
              optimized: false
            };
            
            console.log('[useJobScraping] Transformed job data:', {
              jobId: transformedJobData.jobId,
              title: transformedJobData.title,
              company: transformedJobData.company,
              skillsCount: transformedJobData.skills.length,
              requirementsCount: transformedJobData.requirements.length,
              hasDescription: !!transformedJobData.description,
              portal: scrapedJobData.metadata.portal
            });
            
            setJobDataArray(prev => {
              const newArray = [...prev];
              newArray[index] = {
                url,
                data: transformedJobData,
                error: null
              };
              return newArray;
            });
            
            setJobUrls(prev => {
              const newUrls = [...prev];
              newUrls[index] = { ...newUrls[index], status: 'success', error: null };
              return newUrls;
            });
            
            await logEvent({
              level: 'info',
              action: 'job_fetching_success',
              details: { 
                url,
                index,
                jobId: transformedJobData.jobId,
                jobTitle: transformedJobData.title,
                company: transformedJobData.company,
                skillCount: transformedJobData.skills.length,
                portal: scrapedJobData.metadata.portal,
                langchainParsed: true
              }
            });
            
          } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
              setJobUrls(prev => {
                const newUrls = [...prev];
                newUrls[index] = { 
                  ...newUrls[index], 
                  status: 'failed', 
                  error: 'Scraping cancelled by user' 
                };
                return newUrls;
              });
              
              setJobDataArray(prev => {
                const newArray = [...prev];
                newArray[index] = {
                  url,
                  data: null,
                  error: 'Fetching cancelled by user'
                };
                return newArray;
              });
              
              continue;
            }
            
            setJobUrls(prev => {
              const newUrls = [...prev];
              newUrls[index] = { 
                ...newUrls[index], 
                status: 'failed', 
                error: error instanceof Error ? error.message : 'Unknown error' 
              };
              return newUrls;
            });
            
            setJobDataArray(prev => {
              const newArray = [...prev];
              newArray[index] = {
                url,
                data: null,
                error: error instanceof Error ? error.message : 'Unknown error'
              };
              return newArray;
            });
            
            await logEvent({
              level: 'error',
              action: 'job_fetching_error',
              details: { 
                url,
                index,
                error: error instanceof Error ? error.message : String(error)
              },
              error: error instanceof Error ? error : new Error(String(error))
            });
          }
        }
        
        const allFailed = indicesToScrape.every(index => 
          jobUrls[index].status === 'failed' || jobDataArray[index]?.error
        );
        
        if (allFailed && indicesToScrape.length > 0) {
          setScrapingError('Failed to fetch details from all job URLs. Please check the URLs and try again.');
        } else {
          setScrapingError(null);
        }
        
        await logEvent({
          level: 'info',
          action: 'job_fetching_complete',
          details: { 
            totalUrls: indicesToScrape.length,
            successCount: indicesToScrape.filter(i => jobUrls[i].status === 'success').length,
            errorCount: indicesToScrape.filter(i => jobUrls[i].status === 'failed').length
          }
        });
        
      } catch (error) {
        setScrapingError(error instanceof Error ? error.message : 'Failed to fetch job data. Please check the URLs and try again.');
        
        await logEvent({
          level: 'error',
          action: 'job_fetching_global_error',
          details: { 
            error: error instanceof Error ? error.message : String(error)
          },
          error: error instanceof Error ? error : new Error(String(error))
        });
      } finally {
        setIsScrapingJob(false);
      }
    } catch (error) {
      setScrapingError(error instanceof Error ? error.message : 'An unexpected error occurred');
      setIsScrapingJob(false);
      
      await logEvent({
        level: 'error',
        action: 'job_fetching_unexpected_error',
        details: { 
          error: error instanceof Error ? error.message : String(error)
        },
        error: error instanceof Error ? error : new Error(String(error))
      });
    }
  }, [jobUrls, extractSkillsFromJobData, isValidJobPortalUrl, jobDataArray]);

  // Clear job data
  const clearJobData = useCallback(() => {
    abortControllersRef.current.forEach(controller => controller.abort());
    abortControllersRef.current.clear();
    
    setJobDataArray([]);
    setScrapingError(null);
    setIsScrapingJob(false);
    setJobUrls([{ url: '', status: 'idle', error: null }]);
    
    // Ensure jobDataArray is properly initialized
    setJobDataArray([{ url: '', data: null, error: null }]);
    
    logEvent({
      level: 'info',
      action: 'retry_job_fetching',
      details: {}
    });
  }, []);

  return {
    jobUrls,
    jobDataArray,
    isScrapingJob,
    scrapingError,
    setJobUrls,
    addJobUrl,
    removeJobUrl,
    updateJobUrl,
    handleJobScraping,
    clearJobData,
    cancelScraping,
    retryScraping,
    markJobAsOptimized,
    resetOptimizedStatus,
    validateJobUrl,
    validateAllJobUrls,
    isValidJobPortalUrl,
    hasJobData: (() => {
      const result = jobDataArray.some(item => item.data !== null && item.error === null);
      return result;
    })(),
    validJobCount: (() => {
      const result = jobDataArray.filter(item => item.data !== null && item.error === null).length;
      return result;
    })(),
  };
};
